import React, { FC, memo } from 'react';
import { Group, Text } from 'react-konva';

interface BoxShapeLabelProps {
  label: string;
  areaText: string;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  labelPosition?: { x: number; y: number };
  labelScale?: number;
  labelFontSize?: number;
}

export const BoxShapeLabel: FC<BoxShapeLabelProps> = memo(
  ({ label, areaText, width, height, scaleX, scaleY, labelPosition, labelScale = 1, labelFontSize }) => {
    const displayWidth = Math.abs(width * scaleX);
    const displayHeight = Math.abs(height * scaleY);

    if (!displayWidth || !displayHeight || !scaleX || !scaleY) return null;

    const layoutWidth = displayWidth / labelScale;
    const layoutHeight = displayHeight / labelScale;
    const lines = layoutWidth >= 34 && layoutHeight >= 32 ? [label, areaText] : [label];
    const longestLine = Math.max(...lines.map((line) => line.length), 1);
    const fontSize = Math.min(12, layoutWidth / (longestLine * 0.65 + 1), layoutHeight / (lines.length * 1.6)) * labelScale;
    const hasArea = lines.length > 1;
    const numberFontSize =
      labelFontSize && labelFontSize > 0
        ? Math.min(labelFontSize, displayWidth / (label.length * 0.65 + 1), (displayHeight - (hasArea ? fontSize * 1.3 : 0)) / 1.3)
        : fontSize;
    const textHeight = (numberFontSize + (hasArea ? fontSize : 0)) * 1.3;
    const top = labelPosition
      ? Math.max(0, Math.min(displayHeight - textHeight, labelPosition.y - numberFontSize * 0.65))
      : (displayHeight - textHeight) / 2;

    if (labelFontSize && labelFontSize > 0) {
      return (
        <Group
          x={((labelPosition?.x ?? displayWidth / 2) - displayWidth / 2) / scaleX}
          y={top / scaleY}
          scaleX={1 / scaleX}
          scaleY={1 / scaleY}
          listening={false}
        >
          <Text
            text={label}
            width={displayWidth}
            height={numberFontSize * 1.3}
            fontSize={numberFontSize}
            lineHeight={1.3}
            align="center"
            verticalAlign="middle"
            wrap="none"
          />
          {hasArea && (
            <Text
              text={areaText}
              y={numberFontSize * 1.3}
              width={displayWidth}
              height={fontSize * 1.3}
              fontSize={fontSize}
              lineHeight={1.3}
              align="center"
              verticalAlign="middle"
              wrap="none"
            />
          )}
        </Group>
      );
    }

    return (
      <Text
        text={lines.join('\n')}
        width={displayWidth}
        height={textHeight}
        x={((labelPosition?.x ?? displayWidth / 2) - displayWidth / 2) / scaleX}
        y={top / scaleY}
        scaleX={1 / scaleX}
        scaleY={1 / scaleY}
        fontSize={fontSize}
        lineHeight={1.3}
        align="center"
        verticalAlign="middle"
        listening={false}
      />
    );
  },
);
